import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  ScrollText, Filter, RefreshCw, Calendar, User, Settings, 
  Shield, FileText, Users, DollarSign, Download, Activity
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

// Action type colors
const actionColors = {
  'create': 'bg-emerald-100 text-emerald-800',
  'update': 'bg-blue-100 text-blue-800',
  'delete': 'bg-red-100 text-red-800',
  'login': 'bg-purple-100 text-purple-800',
  'logout': 'bg-slate-100 text-slate-800',
  'export': 'bg-amber-100 text-amber-800',
  'view': 'bg-cyan-100 text-cyan-800',
};

// Entity type icons
const entityIcons = {
  'commission': DollarSign,
  'expense': FileText,
  'agent': Users,
  'referral': Users,
  'user': User,
  'settings': Settings,
  'statement': FileText,
};

export default function AuditLogs() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    entity_type: '',
    user_id: '',
    division: '',
    start_date: '',
    end_date: ''
  });
  const [actionTypes, setActionTypes] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      fetchData();
      fetchFilterOptions();
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const token = getToken();
    
    try {
      // Build query params
      const params = new URLSearchParams();
      if (filters.action) params.append('action', filters.action);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.division) params.append('division', filters.division);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const [logsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/api/audit?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/audit/summary`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setLogs(logsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    const token = getToken();
    try {
      const [actionsRes, entitiesRes] = await Promise.all([
        axios.get(`${API}/api/audit/actions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/audit/entity-types`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setActionTypes(actionsRes.data);
      setEntityTypes(entitiesRes.data);
    } catch (error) {
      console.error('Failed to load filter options');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchData();
  };

  const clearFilters = () => {
    setFilters({
      action: '',
      entity_type: '',
      user_id: '',
      division: '',
      start_date: '',
      end_date: ''
    });
  };

  const exportLogs = () => {
    if (logs.length === 0) {
      toast.error('No logs to export');
      return;
    }
    
    const exportData = logs.map(log => ({
      'Timestamp': new Date(log.timestamp).toLocaleString(),
      'User': log.user_name,
      'Role': log.user_role,
      'Action': log.action,
      'Entity Type': log.entity_type,
      'Entity Name': log.entity_name || '-',
      'Division': log.division || '-',
      'Details': JSON.stringify(log.details || {})
    }));
    
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => headers.map(h => {
        const val = row[h];
        if (typeof val === 'string' && val.includes(',')) return `"${val}"`;
        return val;
      }).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Audit logs exported');
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="p-8 text-center">
          <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">Access Denied</h3>
          <p className="text-slate-500">Only administrators can view audit logs</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ScrollText className="w-7 h-7" />
            Audit Logs
          </h1>
          <p className="text-slate-500">Track all user actions and system changes</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={exportLogs}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <ScrollText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.total_logs}</p>
                  <p className="text-xs text-slate-500">Total Logs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{summary.recent_activity_24h}</p>
                  <p className="text-xs text-slate-500">Last 24 Hours</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {summary.by_action?.slice(0, 2).map((item, idx) => (
            <Card key={idx}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    idx === 0 ? 'bg-purple-100' : 'bg-amber-100'
                  }`}>
                    <FileText className={`w-5 h-5 ${idx === 0 ? 'text-purple-600' : 'text-amber-600'}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{item.count}</p>
                    <p className="text-xs text-slate-500 capitalize">{item.action} Actions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="space-y-2">
              <Label>Action Type</Label>
              <Select value={filters.action || "all"} onValueChange={(v) => handleFilterChange('action', v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {actionTypes.map(action => (
                    <SelectItem key={action} value={action}>{action}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Entity Type</Label>
              <Select value={filters.entity_type || "all"} onValueChange={(v) => handleFilterChange('entity_type', v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Entities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  {entityTypes.map(entity => (
                    <SelectItem key={entity} value={entity}>{entity}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Division</Label>
              <Select value={filters.division || "all"} onValueChange={(v) => handleFilterChange('division', v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Divisions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Divisions</SelectItem>
                  <SelectItem value="mortgage">Mortgage</SelectItem>
                  <SelectItem value="insurance">Insurance</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
              />
            </div>

            <div className="space-y-2 flex items-end gap-2">
              <Button onClick={applyFilters} className="flex-1">
                Apply
              </Button>
              <Button variant="outline" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Activity Log</CardTitle>
          <CardDescription>
            Showing {logs.length} records
          </CardDescription>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-12">
              <ScrollText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700">No Audit Logs</h3>
              <p className="text-slate-500">User actions will be logged here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity Type</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Division</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => {
                    const EntityIcon = entityIcons[log.entity_type] || FileText;
                    return (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm">
                          <div>
                            <p>{new Date(log.timestamp).toLocaleDateString()}</p>
                            <p className="text-xs text-slate-500">{new Date(log.timestamp).toLocaleTimeString()}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{log.user_name}</p>
                            <p className="text-xs text-slate-500 capitalize">{log.user_role}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={actionColors[log.action] || 'bg-slate-100 text-slate-800'}>
                            {log.action}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <EntityIcon className="w-4 h-4 text-slate-400" />
                            <span className="capitalize">{log.entity_type}</span>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {log.entity_name || log.entity_id || '-'}
                        </TableCell>
                        <TableCell>
                          {log.division ? (
                            <Badge variant="outline" className="capitalize">
                              {log.division}
                            </Badge>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          {log.details && Object.keys(log.details).length > 0 ? (
                            <span className="text-xs text-slate-500 truncate block">
                              {JSON.stringify(log.details).substring(0, 50)}...
                            </span>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
